/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: AI Image Generator features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  ctaText: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  ctaText,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={ctaText} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Text-to-Image Made Simple',
      description:
        'Turn ideas into polished visuals with a short prompt and let the AI handle the creative heavy lifting. The text-to-image tool understands subjects, lighting, composition, and mood, so your results feel intentional rather than random. Creators can produce images for social posts, product shots, concept art, or personal projects without needing design skills. High-quality visuals come together quickly, even on tight timelines.',
      ctaText: 'Turn Text to Image Now',
      imageAlt: 'Text to image generation feature',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/text-to-image.webp',
    },
    {
      title: 'Image-to-Image Transformation',
      description:
        "With FalcoCut's image-to-image tool, you can upload a photo or sketch to guide your next creation. The AI reads the shapes and layout from your image and builds on the original structure. Whether you want a new style, a different mood, or cleaner details, the AI adjusts the output while keeping your idea intact. It's an easy way to refine drafts, explore new looks, and keep your visuals consistent across a project.",
      ctaText: 'Turn Image to Image Now',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/image-to-image.webp',
      imageAlt: 'Image to image transformation feature',
    },
    {
      title: 'Guide Images with References',
      description:
        'Shape your images with clear direction by using reference photos to anchor style, pose, and color. FalcoCut reads the structure and visual cues from your reference, then generates new variations that stay aligned with your original idea. This seamless integration of reference data ensures that characters, products, or brand visuals stay consistent across multiple outputs. Creative projects feel more unified and predictable from one generation to the next.',
      ctaText: 'Create Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/reference-guidance.webp',
      imageAlt: 'Reference guidance feature',
    },
    {
      title: 'Edit Any Image by Text Prompts',
      description:
        'Transform images using natural language commands instead of complex tools. FalcoCut can fix old photos, colorize black-and-white shots, remove or add objects, clean backgrounds, upscale details, swap faces, and generate new headshots—all from a single prompt. A wide range of editing options gives creators the flexibility to refine visuals quickly. Creative adjustments feel accessible even without technical editing skills.',
      ctaText: 'Edit Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/edit-by-text.webp',
      imageAlt: 'Edit image by text prompts feature',
    },
    {
      title: 'Resize Images for Any Platform',
      description:
        "Adapt your visuals to every channel with flexible aspect ratios built for TikTok, YouTube, Instagram, and more. High-resolution output keeps images sharp for thumbnails, product shots, posters, and social content. Creators gain full control over size and quality to match each platform's requirements, ensuring every visual looks polished and ready to publish. A wide range of size options supports consistent quality across all your projects.",
      ctaText: 'Create Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/image-resize.webp',
      imageAlt: 'Image resize for any platform feature',
    },
    {
      title: 'Smarter AI Model Support',
      description:
        'Access advanced models like Nano Banana Pro and Flux Kontext Pro to bring ideas to life with clarity and detail. These models understand complex prompts and adapt to different visual styles—realistic, artistic, anime-inspired, or cinematic—without relying on presets. The result is flexible, expressive image generation that follows your creative direction naturally. Creators gain more freedom to experiment because the models respond reliably to subtle mood, texture, and storytelling cues in every prompt.',
      ctaText: 'Create Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/ai-model-support.webp',
      imageAlt: 'Smarter AI model support feature',
    },
    {
      title: 'Built-In Asset Library for Consistency',
      description:
        'Build a personal library of reference images to maintain character designs, product details, or brand identity across multiple generations. Reusing uploaded assets makes it easier to stay visually consistent from one project to the next. This saves time and keeps your creative direction unified, even across large batches of images. A stable visual identity helps audiences recognize your work instantly, strengthening brand presence across every platform you publish on.',
      ctaText: 'Create Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/asset-library.webp',
      imageAlt: 'Built-in asset library feature',
    },
    {
      title: 'Ready for Any Creative Task',
      description:
        'Produce everything from product photos and character art to marketing visuals and social content without switching platforms. The workspace adapts to simple prompts, detailed concepts, or large multi-task projects, giving creators room to explore ideas at any scale. You can move from early sketches to polished final images without changing platforms. A broad range of capabilities keeps your workflow flexible, whether the project is simple, experimental, or large-scale.',
      ctaText: 'Create Your Image',
      imageUrl: 'https://assets.falcocut.ai/ai-image-generator/creative-workspace.webp',
      imageAlt: 'Ready for any creative task feature',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful Features That Make FalcoCut Stand Out
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          FalcoCut brings advanced AI models, flexible controls, and an integrated set of
          creative tools into one smooth workflow. Every feature is designed to help you
          create high-quality images faster, with less effort and more freedom to explore
          your ideas.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              ctaText={feature.ctaText}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
